import { useProductList } from '~/features/experiments/hooks/use-product-list';
import type { ProductListParams } from '~/features/experiments/types/product-list.types';

interface UseBeforeEnrichmentProductsArgs {
  /** GMC/platform connection UUID — resolved from `useEnrichmentContext().connectionId`. */
  connectionId: string;
  /** Catalog snapshot pin — resolved from `useEnrichmentContext().syncId`. */
  syncId?: string | null | undefined;
  /** Platform-native ids (`externalId`) for the currently-rendered page of enriched products. */
  externalIds: string[];
}

/** Fetches pre-enrichment ("before") catalog records for the current page via the shared
 *  `products/{connectionId}/list` endpoint, filtered to the given externalIds. Always requests
 *  page 1 sized to exactly `externalIds.length` — the exact-match filter means every match fits
 *  in one page, so the table's own pagination doesn't apply here. Disabled until both a
 *  connectionId and at least one externalId are available. */
export function useBeforeEnrichmentProducts({
  connectionId,
  syncId,
  externalIds,
}: UseBeforeEnrichmentProductsArgs) {
  const params: ProductListParams | null =
    connectionId && externalIds.length > 0
      ? {
          connectionId,
          page: 1,
          size: externalIds.length,
          appliedFilters: { product_id: externalIds },
          syncId: syncId ?? null,
        }
      : null;

  const { data, ...rest } = useProductList(params);
  return { data: data?.products, ...rest };
}
